/**
 * GridCrash — Ghost Racing
 *
 * Races a recorded top-run replay live against the player. The ghost
 * plays its OWN board (mine layout depends on seed + first click, so
 * two runs on the same seed can differ) in a hidden ReplayPlayer — the
 * race is a pace race: progress-over-time, not cell-for-cell.
 *
 * Timing: the ghost's clock starts at the player's first reveal, the
 * same anchor as the live game timer. Both ReplayPlayer's rAF clock
 * and the engine timer are wall-clock, so tab-switching advances both
 * equally and the race stays fair.
 *
 * Scores are hidden mid-game (engine computes them at WIN/LOSE), so the
 * live ghost readout is its flag-score (correct×10 − wrong×5 — the
 * same metric the versus CPU panel shows). The final verdict compares
 * the player's end-of-game score against the ghost's recorded
 * final_score — never waits for ghost playback to finish.
 *
 * Pure controller — rendering is race_bar.ts's job via onProgress.
 */

import { DIFFICULTIES, type GameState } from "./engine.js";
import { ReplayPlayer } from "./replay_player.js";
import { isWatchable, type ReplayEvent, type ReplayMeta } from "./replay.js";

export interface GhostSnapshot {
  /** 0..1 — share of the ghost's safe cells revealed. */
  progress: number;
  /** Live flag-score on the ghost's board. */
  liveScore: number;
  finished: boolean;
}

export type RaceOutcome = "player" | "ghost" | "tie" | "player_detonated";

export class GhostRace {
  private player: ReplayPlayer;
  private started = false;
  private ended = false;
  private lastEventIdx = -1;
  private lastLiveScore = 0;

  onProgress: ((snap: GhostSnapshot) => void) | null = null;
  onRaceEnd: ((outcome: RaceOutcome, playerScore: number, ghostScore: number) => void) | null = null;

  readonly ghostName: string;
  readonly ghostFinalScore: number;
  readonly replayId: string;

  /** Rejects streams that can't be replayed safely (ReplayPlayer doesn't bounds-check). */
  static validate(meta: ReplayMeta, events: ReplayEvent[]): boolean {
    if (!isWatchable(meta) || events.length === 0) return false;
    const d = DIFFICULTIES[meta.engine_difficulty!];
    if (!d || !Number.isFinite(Number(meta.seed))) return false;
    return events.every(
      (ev) =>
        (ev.a === "r" || ev.a === "f") &&
        Number.isInteger(ev.x) && ev.x >= 0 && ev.x < d.cols &&
        Number.isInteger(ev.y) && ev.y >= 0 && ev.y < d.rows &&
        Number.isFinite(ev.t) && ev.t >= 0
    );
  }

  /** Caller must validate() first. */
  constructor(meta: ReplayMeta, events: ReplayEvent[]) {
    this.ghostName = meta.player_name ?? "Anonymous";
    this.ghostFinalScore = meta.final_score;
    this.replayId = meta.id;
    this.player = new ReplayPlayer(
      DIFFICULTIES[meta.engine_difficulty!],
      meta.mode!,
      Number(meta.seed),
      events
    );
    this.player.onUpdate = (state) => this.emitProgress(state, false);
    this.player.onEnded = () => this.emitProgress(this.player.getState(), true);
  }

  /**
   * Feed every live-engine state change here. Starts the ghost clock on
   * the player's first reveal; settles the race when the player's game
   * ends.
   */
  onPlayerState(state: Readonly<GameState>): void {
    if (this.ended) return;

    if (!this.started && state.phase === "PLAY") {
      this.started = true;
      this.player.play();
      return;
    }

    if (state.phase === "WIN" || state.phase === "LOSE") {
      this.ended = true;
      this.player.pause();
      if (state.phase === "LOSE") {
        this.onRaceEnd?.("player_detonated", state.score, this.ghostFinalScore);
      } else {
        const outcome: RaceOutcome =
          state.score > this.ghostFinalScore ? "player"
          : state.score < this.ghostFinalScore ? "ghost"
          : "tie";
        this.onRaceEnd?.(outcome, state.score, this.ghostFinalScore);
      }
    }
  }

  /** Joins a race already in progress (ghost fetched after first reveal). */
  attachLate(msSinceFirstReveal: number): void {
    if (this.started || this.ended) return;
    this.started = true;
    this.player.seekTo(Math.max(0, msSinceFirstReveal));
    this.player.play();
  }

  destroy(): void {
    this.ended = true;
    this.onProgress = null;
    this.onRaceEnd = null;
    this.player.destroy();
  }

  // ─── Internals ────────────────────────────────────────────────────────────────

  private emitProgress(state: Readonly<GameState>, finished: boolean): void {
    const d = state.difficulty;
    const totalSafe = d.rows * d.cols - d.mineCount;
    const progress = finished
      ? 1
      : Math.min(1, Math.max(0, 1 - state.safeCellsRemaining / totalSafe));

    // The flag scan is O(cells); rescan only when an event landed.
    // flagsPlaced/safeCellsRemaining move iff the grid changed, so they
    // make a cheap change key across 60fps onUpdate calls.
    const key = state.flagsPlaced * 100000 + state.safeCellsRemaining;
    if (key !== this.lastEventIdx) {
      this.lastEventIdx = key;
      let score = 0;
      for (const row of state.grid) {
        for (const cell of row) {
          if (cell.isFlagged) score += cell.isMine ? 10 : -5;
        }
      }
      this.lastLiveScore = Math.max(0, score);
    }

    this.onProgress?.({ progress, liveScore: this.lastLiveScore, finished });
  }
}
